import { Button } from '@chakra-ui/button';
import Icon from '@chakra-ui/icon';
import { Img } from '@chakra-ui/image';
import {
  Box,
  Flex,
  Heading,
  HeadingProps,
  Link,
  SimpleGrid,
  Text,
} from '@chakra-ui/layout';
import { AspectRatio } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import ReactPixel from 'react-facebook-pixel';
import { ArrowRight } from 'react-feather';
import { Footer } from '../../../components/layout/Footer';
import { useConfig } from '../../../hooks/useConfig';
import { useCourses } from '../../../hooks/useCourses';

const DARK = '#131313';

const Headline: React.FC<{ title: string } & HeadingProps> = ({
  title,
  ...rest
}) => (
  <Heading
    alignSelf="flex-start"
    mt={[16, 16, 24]}
    mb={[8, 8, 12]}
    size="lg"
    w="full"
    textAlign="center"
    fontFamily="'Pacifico'"
    {...rest}
  >
    {title}
  </Heading>
);

const Card: React.FC<{
  title?: string;
  description: string;
  image?: string;
  buttonText?: string;
  cta?: string;
  fullImageHeight?: boolean;
}> = ({
  title,
  description,
  image,
  buttonText,
  cta,
  fullImageHeight,
  children,
}) => (
  <Flex
    direction="column"
    w="full"
    h="full"
    justify="space-between"
    p={4}
    borderRadius="xl"
    boxShadow="xl"
    transition="all .2s ease"
    _hover={{ transform: 'scale(1.02)', boxShadow: '2xl' }}
    align="flex-start"
  >
    <Flex direction="column" w="full">
      {title && (
        <Heading size="md" mb={6}>
          {title}
        </Heading>
      )}
      {image &&
        (!fullImageHeight ? (
          <Flex
            mb={6}
            h="200px"
            overflow="hidden"
            borderRadius="md"
            bgImage={`url("${image}")`}
            w="full"
            bgSize="cover"
            bgPosition="center"
          />
        ) : (
          <Img mb={6} src={image} borderRadius="md" overflow="hidden" />
        ))}
      {children}

      {description.split('#n').map((desc, idx) => (
        <Text mb={2} key={`desc${idx.toString()}`} w="full">
          {desc}
        </Text>
      ))}
    </Flex>

    {cta && (
      <Button
        w="full"
        colorScheme="brand"
        leftIcon={<Icon as={ArrowRight} />}
        as={Link}
        mt={6}
        href={cta}
        onClick={() => ReactPixel.trackCustom('submitLandingPage')}
      >
        {buttonText || 'Mehr dazu'}
      </Button>
    )}
  </Flex>
);

const Hero: React.FC<{ description: string; image?: string }> = ({
  description,
  image,
}) => (
  <Box w="full" zIndex={1}>
    <Box
      w="full"
      bgImage={`url("${image}")`}
      bgSize="cover"
      bgPosition="center"
    >
      <Flex bg="#00000099">
        <Flex
          m="0 auto"
          align="center"
          justify="center"
          color="white"
          w="full"
          px={[4, 6]}
          maxW="1200px"
          direction="column"
          overflow="hidden"
        >
          <Heading
            fontFamily="'Pacifico'"
            mt={['calc(18vh + 90px)']}
            textAlign="center"
            fontWeight="normal"
            size="2xl"
          >
            next level online-fitness
          </Heading>
          <Text
            align="center"
            textShadow="0px 0px 4px black"
            mt={[6, 8, 12]}
            mb={[12, 12, '22vh']}
            fontSize="lg"
          >
            {description}
          </Text>
        </Flex>
      </Flex>
    </Box>
  </Box>
);

interface CommonLandingPageProps {}

export const CommonLandingPage: React.FC<CommonLandingPageProps> = () => {
  const config = useConfig();
  const courses = useCourses();

  useEffect(() => {
    ReactPixel.trackCustom('landingPageShowed');
  }, []);

  const filteredCourses =
    courses.courses &&
    courses.courses.filter(
      (course) => course.recurrence.isRecurring && course.isOnline
    );

  return (
    <>
      <Hero
        image={config?.landingHeader.imgUrl}
        description={`Ob ${
          filteredCourses
            ? filteredCourses[
                Math.floor(Math.random() * filteredCourses.length)
              ].title
            : 'Yoga'
        } im Wohnzimmer, ${
          filteredCourses
            ? filteredCourses[
                Math.floor(Math.random() * filteredCourses.length)
              ].title
            : 'Pilates'
        } im Park oder ${
          filteredCourses
            ? filteredCourses[
                Math.floor(Math.random() * filteredCourses.length)
              ].title
            : 'HIIT'
        } auf Arbeit oder im Urlaub  – mit unseren Online-Fitnessprogrammen kannst du deine eigene Welt in dein Fitnessstudio verwandeln. Entdecke eine Vielzahl an motivierenden Kursen, um jederzeit und überall fit zu bleiben.`}
      />
      {/* <Box m="0 auto" maxW="1200px" px={[0, 0, 6]}>
        <Box
          position="relative"
   
          boxShadow="xl"
          borderRadius={[0, 0, 'lg']}
          overflow="hidden"
          top={[0, 0, '-16vh']}
          h="-50px"
        >
          <Image
           src={config?.hero.imgUrl}
           w="full"
          />
        </Box>
      </Box> */}

      <Box px={[4, 6]} maxW="1200px" m="0 auto" color={DARK}>
        {/* <Flex w="full" justify="center">
<Logo alignSelf="center" maxW="100px" />
</Flex> */}

        <Headline title="was wird dir geboten?" />
        <SimpleGrid columns={[1, 1, 3]} gap={4}>
          <Card
            title={`${
              filteredCourses?.length || '35+'
            } Live-Kurse in der Woche`}
            description="Erlebe unsere Kurswelt jederzeit und von überall. Mit einem Klick bist du mittendrin. Dein neuer Lieblingskurs wartet schon auf dich."
            image={
              (filteredCourses &&
                filteredCourses[
                  Math.floor(Math.random() * filteredCourses.length)
                ].img) ||
              ''
            }
            buttonText="Kurswelt erleben"
            cta="/login"
          />
          <Card
            title="Qualifizierte Trainer"
            description="Unsere erfahrenen Trainer haben immer den perfekten Durchblick, um dich ideal im Training zu unterstützen und zu führen."
            image={
              (filteredCourses &&
                filteredCourses[
                  Math.floor(Math.random() * filteredCourses.length)
                ].img) ||
              ''
            }
            buttonText="Jetzt loslegen"
            cta="/login"
          />
          <Card
            title="Sei live dabei!"
            description="Schalte einfach deine Kamera ein, lasse dich von deinem Trainer coachen und erreiche mit uns gemeinsam deine Ziele!"
            image={
              (filteredCourses &&
                filteredCourses[
                  Math.floor(Math.random() * filteredCourses.length)
                ].img) ||
              ''
            }
            buttonText="Will ich nicht verpassen!"
            cta="/login"
          />
        </SimpleGrid>
        <Flex zIndex="100" mb={8} direction="column">
          <Headline
            title={`hybrides training in deinem ${config?.displayName.toLowerCase()}`}
          />
          <Card
            title="Was bedeutet hybrides Training?"
            description={`Mit unserem neuen hybriden Trainingssystem kannst du von überall an unseren Kursen teilnehmen oder dich über die Kurse im ${config?.displayName} einfach nur informieren. Alles, was du dafür benötigst ist eine gute Internetverbindung und ein Endgerät, wie dein Smartphone, dein Tablet oder dein Laptop.
           #nWenn du willst, schalte deine Kamera an, dann kann dich der Trainer sehen, mit dir interagieren und du bist mittendrin! Dafür registrierst du dich einfach in unserem Onlineportal.
           #nBist du Mitglied im ${config?.displayName}, legst du ein Passwort fest und kannst danach direkt kostenlos durchstarten.
          #nWenn du kein Mitglied sein solltest, kannst du unsere Kurse kostenfrei für ${config?.trialExpiration} testen. Danach zahlst du nur ${config?.onlineMembershipFee} pro Monat für die Nutzung.
            `}
            buttonText="Jetzt durchstarten"
            cta="/login"
          />
        </Flex>

        <Flex direction="column" mb={10}>
          <Headline title="einblick in die kurswelt" />

          <Card
            description="Hier hast du einen Einblick in unsere Kurswelt."
            buttonText="Jetzt durchstarten"
            cta="/login"
          >
            {config?.landingYouTubeVideo && (
              <AspectRatio ratio={16 / 9} mb={6}>
                <iframe
                  width="560"
                  height="315"
                  src={`https://www.youtube.com/embed/${config.landingYouTubeVideo}`}
                  title="kursvideo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </AspectRatio>
            )}
          </Card>
        </Flex>
      </Box>
      <Footer />
    </>
  );
};
